import { ArrowLeftOutlined, UserOutlined } from "@ant-design/icons"
import { Breadcrumb, Button, Space, Select } from "antd"
import React, { FC, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, matchPath, useHistory, useLocation } from "react-router-dom"
import { GetCompanyQuery, useGetCompanyQuery } from "../../generated/graphql"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import {
    URL_ACCOUNT,
    URL_ACCOUNT_BOOKINGS,
    URL_BASE,
    URL_BOOKING_SUMMARY,
    URL_COURSE_DETAILS,
    URL_FORGOT_PASSWORD,
    URL_INTEREST_DETAILS,
    URL_INTEREST_REGISTRATION,
    URL_LOGIN,
    URL_REGISTER,
    URL_RESULTS,
} from "../../models/url"
import { margin } from "../../styles/layout"
import { LocationState } from "../../types"
import { safeStorage } from "../../helpers/safeStorage"
import { identifyLogRocket } from "../../helpers/logrocket"
import { postMessageToParent } from "../../helpers/safePostMessage"
import Logout from "../../views/Logout/Logout"
import css from "./AuthHeader.module.css"
import { blue } from "@ant-design/colors"
import packageJson from "../../../package.json"
import { persistLoginFromUrl, resolveLoginFromUrl } from "../login/loginFromUrl"

type AuthHeaderProps = { currentLocation: string | undefined; htmlId?: string }

const AuthHeader: FC<AuthHeaderProps> = ({ currentLocation, htmlId }) => {
    const { t, i18n } = useTranslation()
    const history = useHistory()
    const isLoggedIn = !!safeStorage.getItem("loggedInUser")
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const location = useLocation<LocationState>()
    const { pathname } = location
    const isAuthFlowPath = pathname.startsWith(URL_LOGIN) || pathname.startsWith(URL_REGISTER) || pathname.startsWith(URL_FORGOT_PASSWORD)
    // Beim Wechsel zwischen Login und Registrierung darf nicht die aktuelle
    // Auth-Route das bereits gespeicherte fachliche Rücksprungziel ersetzen.
    const authFromUrl = isAuthFlowPath ? resolveLoginFromUrl(location.state?.fromUrl) : { pathname, search: location.search || undefined }

    const forceRefresh = () => {
        console.log("forceRefresh")
        postMessageToParent("removeParameters")
        history.replace(currentLocation ?? pathname)
        // window.location.reload()
    }

    const mediaIsGreaterThanXs = media === "sm" || media === "md" || media === "lg" || media === "xl" || media === "xxl"

    const { data: companyData } = useGetCompanyQuery()
    const companyName: GetCompanyQuery["companyPublic"]["name"] = companyData?.companyPublic.name ?? ""

    // BackTo-URL: Query-Parameter "backTo" (case-insensitive) hat Vorrang, sonst companyPublic.website aus der DB.
    // Ersetzt die frühere /external/-Wrapper-Seite, die pro Kunde einen Zurück-Button hatte.
    // WICHTIG: Button erscheint NUR, wenn die App via /external/ aufgerufen wurde (nginx setzt "external=1").
    // Damit bleibt der Button bei iFrame-Einbindungen auf Kunden-Websites unsichtbar, wie im alten Verhalten.
    const isExternalEntry: boolean = (() => {
        try {
            const params = new URLSearchParams(window.location.search)
            let flag: string | null = null
            params.forEach((value, key) => {
                if (!flag && key.toLowerCase() === "external") flag = value
            })
            return flag === "1" || flag === "true"
        } catch {
            return false
        }
    })()

    const backToUrl: string | undefined = (() => {
        if (!isExternalEntry) return undefined
        try {
            const params = new URLSearchParams(window.location.search)
            let fromQuery: string | null = null
            params.forEach((value, key) => {
                if (!fromQuery && key.toLowerCase() === "backto") fromQuery = value
            })
            if (fromQuery && /^https?:\/\//i.test(fromQuery)) return fromQuery
        } catch {
            // URLSearchParams-Fehler ignorieren
        }
        const fromDb = companyData?.companyPublic.website
        if (fromDb && /^https?:\/\//i.test(fromDb)) return fromDb
        return undefined
    })()

    const backToLabel: string | undefined = (() => {
        if (!backToUrl) return undefined
        try {
            return new URL(backToUrl).hostname.replace(/^www\./i, "")
        } catch {
            return backToUrl
        }
    })()

    // Prominenter vollbreiter Zurueck-Button, wie frueher in den /external/ Wrapper-Seiten.
    // Sticky oben, Ant Design Primary-Blau, zentrierter Text.
    const BackToButton: FC = () =>
        backToUrl ? (
            <a
                href={backToUrl}
                style={{
                    display: "block",
                    width: "100%",
                    padding: "10px 16px",
                    marginBottom: 8,
                    backgroundColor: "#1890ff",
                    color: "#ffffff",
                    textAlign: "center",
                    textDecoration: "none",
                    fontWeight: 500,
                    borderRadius: 4,
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                    boxSizing: "border-box",
                }}
            >
                <ArrowLeftOutlined style={{ marginRight: 8 }} />
                {t("nav.backTo", "Zurück zu")} {backToLabel}
            </a>
        ) : null

    // Kam der Nutzer aus dem Konto (z.B. "Details" bei einer Buchung), bringt
    // location.state.fromUrl den Rücksprungpfad mit. Wir zeigen dann denselben
    // "Zurück zu"-Balken wie BackToButton, aber als interne react-router-Navigation.
    const accountBackTo = (() => {
        const from = location.state?.fromUrl
        const path = typeof from === "string" ? from : from?.pathname
        if (!path || !path.startsWith(URL_ACCOUNT)) return undefined
        return { pathname: path, search: (typeof from === "string" ? undefined : from?.search) ?? "" }
    })()
    const accountBackLabel = accountBackTo?.pathname.startsWith(URL_ACCOUNT_BOOKINGS)
        ? t("account.backToBookings", "Zurück zu meinen Buchungen")
        : t("account.backToDashboard", "Zurück zu meinem Konto")

    const AccountBackLink: FC = () =>
        accountBackTo ? (
            <Link
                to={accountBackTo}
                // goBack statt push/replace: fromUrl existiert nur nach In-App-Push, der vorherige
                // History-Eintrag ist also exakt die Herkunftsseite (inkl. ?tab=past im URL-Query).
                // push erzeugte einen Bounce, replace einen Duplikat-Eintrag [Konto, Buchungen, Buchungen] —
                // erst goBack lässt Browser-Zurück nach dem Rücksprung direkt beim Konto landen.
                onClick={(event) => {
                    event.preventDefault()
                    history.goBack()
                }}
                data-testid='course-account-back'
                style={{
                    display: "block",
                    width: "100%",
                    padding: "10px 16px",
                    marginBottom: 8,
                    backgroundColor: "#1890ff",
                    color: "#ffffff",
                    textAlign: "center",
                    textDecoration: "none",
                    fontWeight: 500,
                    borderRadius: 4,
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                    boxSizing: "border-box",
                }}
            >
                <ArrowLeftOutlined style={{ marginRight: 8 }} />
                {accountBackLabel}
            </Link>
        ) : null

    type BreadcrumbsProps = { style?: React.CSSProperties }
    const Breadcrumbs: FC<BreadcrumbsProps> = ({ style }) => {
        // AuthHeader hängt in MainLayout — NICHT unter der :id-Route. useParams läge
        // hier also leer und erzeugte einen kaputten "/details/"-Link. Deshalb die
        // Kurs-ID direkt aus dem Pfad matchen (wie unten für die Interest-Routen).
        const courseDetailsMatch = matchPath<{ id: string }>(pathname, { path: URL_COURSE_DETAILS, exact: true })
        const bookingSummaryMatch = matchPath<{ courseId: string }>(pathname, { path: URL_BOOKING_SUMMARY, exact: true })
        const courseId = courseDetailsMatch?.params.id ?? bookingSummaryMatch?.params.courseId ?? ""
        const courseDetailsUrl = courseId ? URL_COURSE_DETAILS.replace(":id", courseId) : URL_BASE
        const interestRegistrationMatch = matchPath<{ id: string }>(pathname, { path: URL_INTEREST_REGISTRATION, exact: true })
        const interestDetailsMatch = matchPath<{ id: string }>(pathname, { path: URL_INTEREST_DETAILS, exact: true })
        const interestId = interestRegistrationMatch?.params.id ?? interestDetailsMatch?.params.id
        const interestDetailsUrl = interestId ? URL_INTEREST_DETAILS.replace(":id", interestId) : URL_BASE

        const styleMobile: React.CSSProperties = mediaIsGreaterThanXs ? {} : { marginBottom: margin.min /* paddingBottom: margin.min */ }

        const actualStyle: React.CSSProperties = { ...styleMobile, ...style }

        if (pathname === `${URL_BASE}` /* && mediaIsGreaterThanXs */)
            return (
                <Breadcrumb style={actualStyle}>
                    <Breadcrumb.Item>
                        <Link to={URL_BASE}>{t("nav.search", "Kurssuche")}</Link>
                    </Breadcrumb.Item>
                </Breadcrumb>
            )

        if (pathname.startsWith(`${URL_BASE}details/`))
            return (
                <Breadcrumb style={actualStyle}>
                    <Breadcrumb.Item>
                        <Link to={URL_BASE}>{t("nav.search", "Kurssuche")}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to={courseDetailsUrl}>{t("nav.details", "Kursdetails")}</Link>
                    </Breadcrumb.Item>
                </Breadcrumb>
            )

        if (interestId)
            return (
                <Breadcrumb style={actualStyle}>
                    <Breadcrumb.Item>
                        <Link to={URL_BASE}>{t("nav.search", "Kurssuche")}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        {interestRegistrationMatch ? (
                            <Link to={interestDetailsUrl}>{t("interest.details.breadcrumb", "Details zur Interessenten-Liste")}</Link>
                        ) : (
                            t("interest.details.breadcrumb", "Details zur Interessenten-Liste")
                        )}
                    </Breadcrumb.Item>
                    {interestRegistrationMatch && <Breadcrumb.Item>{t("interest.flow.title", "Interesse bekunden")}</Breadcrumb.Item>}
                </Breadcrumb>
            )

        if (pathname.startsWith(`${URL_BASE}bookingsummary/`))
            return (
                <Breadcrumb style={actualStyle}>
                    <Breadcrumb.Item>
                        <Link to={URL_BASE}>{t("nav.search", "Kurssuche")}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to={courseDetailsUrl}>{t("nav.details", "Kursdetails")}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to={URL_BOOKING_SUMMARY}>Buchungsübersicht</Link>
                    </Breadcrumb.Item>
                </Breadcrumb>
            )

        return (
            <Breadcrumb style={actualStyle}>
                <Breadcrumb.Item>
                    <Link to={URL_BASE}>
                        <Button type='link' size='small'>
                            {t("nav.backToSearch", "Zur Kurssuche")}
                        </Button>
                    </Link>
                </Breadcrumb.Item>
            </Breadcrumb>
        )
    }

    const version = packageJson.version

    // LogRocket-Identify hängt am Allowlist-Gate im Helper (Issue #93); uid = booker.id.
    useEffect(() => {
        if (!isLoggedIn) return
        const bookerString = safeStorage.getItem("booker")
        if (!bookerString) return
        const booker = JSON.parse(bookerString)
        if (!booker?.id) return
        void identifyLogRocket({
            uid: String(booker.id),
            traits: {
                companyName,
                name: `${booker.firstname} ${booker.lastname}`,
                email: booker.email,
                customerId: booker.customerId,
                organizationId: booker.organizationId,
                bookerId: booker.id,
                version,
            },
        })
    }, [isLoggedIn, companyName, version])

    const renderContent = (isLoggedIn: boolean) => {
        if (isLoggedIn) {
            const booker = JSON.parse(safeStorage.getItem("booker") as string)

            if (!booker) {
                console.log("booker not found")
                // localStorage.removeItem("loggedInUser")
                history.push(URL_RESULTS.replace(":resultType", "loggedOut"))
                return null
            }

            return (
                <>
                    <BackToButton />
                    <AccountBackLink />
                    <div className={css.root} id={htmlId}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                // flexDirection: mediaIsGreaterThanXs ? "row" : "column-reverse",
                                flexDirection: "row",
                                // 320px: "abmelden" darf umbrechen statt aus dem Viewport zu ragen.
                                flexWrap: mediaIsGreaterThanXs ? "nowrap" : "wrap",
                            }}
                        >
                            <Breadcrumbs />
                        <Space align='center' size='small' wrap style={{ justifyContent: "right", paddingBottom: 7 }}>
                            {mediaIsGreaterThanXs ? (
                                <>
                                    <Link to={URL_ACCOUNT}>
                                        <Button
                                            size='small'
                                            shape='round'
                                            style={{
                                                alignSelf: "top",
                                                display: "flex",
                                                alignItems: "center",
                                                color: blue[5],
                                                borderColor: blue[2],
                                            }}
                                            className={css.accountButton}
                                        >
                                            <UserOutlined style={{ marginRight: 5 }} />
                                            <div className={css.loggedInAs} style={{ marginRight: 5 }}>
                                                {t("nav.account", "Benutzerkonto")}
                                            </div>
                                        </Button>
                                    </Link>
                                </>
                            ) : (
                                <Link to={URL_ACCOUNT}>
                                    <Button
                                        size='small'
                                        shape='round'
                                        style={{
                                            alignSelf: "top",
                                            display: "flex",
                                            alignItems: "center",
                                            color: blue[5],
                                            borderColor: blue[2],
                                        }}
                                        className={css.accountButton}
                                    >
                                        <UserOutlined /* style={{ marginRight: 5 }} */ />
                                        <div className={css.loggedInAs}>{t("nav.account", "Benutzerkonto")}</div>
                                    </Button>
                                </Link>
                            )}
                            <Logout style={{ alignSelf: "top", marginRight: 7 }} forceRefresh={() => forceRefresh()} />
                        </Space>
                    </div>
                </div>
                </>
            )
        }

        return (
            <>
                <BackToButton />
                <div className={css.root} id={htmlId}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            flexDirection: mediaIsGreaterThanXs ? "row" : "column-reverse",
                        }}
                    >
                        <Breadcrumbs />
                    <Space align='center' wrap style={{ justifyContent: "right", paddingBottom: 7 }}>
                        <Link to={URL_REGISTER} onClick={() => persistLoginFromUrl(authFromUrl)}>
                            <Button size='small' type='link' style={{ alignSelf: "top" }}>
                                {t("auth.register", "registrieren")}
                            </Button>
                        </Link>
                        <Link to={URL_LOGIN} onClick={() => persistLoginFromUrl(authFromUrl)}>
                            <Button size='small' type='primary' ghost style={{ alignSelf: "top" }} data-testid='login-button'>
                                {t("auth.login", "anmelden")}
                            </Button>
                        </Link>
                        {/* <Select
                            size='small'
                            value={i18n.language.startsWith("de") ? "de" : "en"}
                            showArrow={false}
                            onChange={(lng: string) => {
                                const target = lng === "de" ? "de" : "en"
                                i18n.changeLanguage(target)
                                safeStorage.setItem("i18n", target)
                                window.location.reload()
                            }}
                            style={{ padding: 0, marginLeft: `calc(${margin.xs}px * -1)`, marginRight: mediaIsGreaterThanXs ? `calc(${margin.xs}px * -1)` : 0 }}
                            dropdownMatchSelectWidth={false}
                            bordered={false}
                        >
                            <Select.Option value='de'>🇩🇪</Select.Option>
                            <Select.Option value='en'>🇬🇧</Select.Option>
                        </Select> */}
                    </Space>
                </div>
                </div>
            </>
        )
    }

    return renderContent(isLoggedIn)
}

export default AuthHeader
